import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './WhaleSummaryModal.css';

function WhaleSummaryModal({ show, onClose, summary: summaryProp }) {
  const { t } = useTranslation();
  const [transformedSummary, setTransformedSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [animatedValues, setAnimatedValues] = useState({
    totalVolume: 0,
    largestTrade: 0,
    netFlow: 0
  });
  const [lastUpdateText, setLastUpdateText] = useState('');
  const [summaryTimestamp, setSummaryTimestamp] = useState(null);

  // Process summary when it changes
  useEffect(() => {
    if (!summaryProp) return;

    setLoading(true);

    // Transform new summary structure to old format for compatibility
    const transformed = {
      totalVolume: summaryProp.whaleTrades?.totalVolume || 0,
      tradeCount: summaryProp.whaleTrades?.count || 0,
      largestTrade: summaryProp.whaleTrades?.largestTrade || null,
      buyVolume: summaryProp.whaleTrades?.buyVolume || 0,
      sellVolume: summaryProp.whaleTrades?.sellVolume || 0,
      netFlow: (summaryProp.whaleTrades?.buyVolume || 0) - (summaryProp.whaleTrades?.sellVolume || 0),
      momentum: null
    };

    setTransformedSummary(transformed);
    setSummaryTimestamp(summaryProp.timestamp ? new Date(summaryProp.timestamp) : new Date());
    animateNumbers(transformed);
    setLoading(false);
  }, [summaryProp]);

  // Update "last updated" text every second
  useEffect(() => {
    if (!summaryTimestamp) return;

    const updateLastUpdateText = () => {
      const now = new Date();
      const diffMs = now - summaryTimestamp;
      const diffSec = Math.floor(diffMs / 1000);

      if (diffSec < 60) {
        setLastUpdateText(t('time.secondsAgo', { count: diffSec }));
      } else if (diffSec < 3600) {
        const mins = Math.floor(diffSec / 60);
        setLastUpdateText(t('time.minutesAgo', { count: mins }));
      } else {
        const hours = Math.floor(diffSec / 3600);
        setLastUpdateText(t('time.hoursAgo', { count: hours }));
      }
    };

    updateLastUpdateText();
    const interval = setInterval(updateLastUpdateText, 1000);

    return () => clearInterval(interval);
  }, [summaryTimestamp, t]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [show]);

  // Animate numbers counting up
  const animateNumbers = (data) => {
    const duration = 1500; // 1.5 seconds
    const steps = 60;
    const interval = duration / steps;

    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;

      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);

      setAnimatedValues({
        totalVolume: Math.floor(data.totalVolume * easeOut),
        largestTrade: data.largestTrade ? Math.floor(data.largestTrade.dollarSize * easeOut) : 0,
        netFlow: Math.floor(data.netFlow * easeOut)
      });

      if (currentStep >= steps) {
        clearInterval(timer);
      }
    }, interval);
  };

  // Calculate market bias
  const calculateMarketBias = (buyVolume, sellVolume) => {
    if (!buyVolume && !sellVolume) {
      return { bias: 'neutral', message: t('whaleSummary.noData') };
    }

    const totalVolume = buyVolume + sellVolume;
    const difference = Math.abs(buyVolume - sellVolume);
    const differencePercent = (difference / totalVolume) * 100;

    // If difference is less than 20%, consider it neutral
    if (differencePercent < 20) {
      return {
        bias: 'neutral',
        message: `${t('whaleSummary.balanced')} (${formatCurrency(buyVolume)} ${t('whaleSummary.longVsShort')} ${formatCurrency(sellVolume)} ${t('whaleTrades.short').toLowerCase()})`
      };
    }

    // Determine if long or short biased
    if (buyVolume > sellVolume) {
      return {
        bias: 'long',
        message: `${t('whaleSummary.longBiased')} (${formatCurrency(buyVolume)} ${t('whaleSummary.longVsShort')} ${formatCurrency(sellVolume)} ${t('whaleTrades.short').toLowerCase()})`
      };
    } else {
      return {
        bias: 'short',
        message: `${t('whaleSummary.shortBiased')} (${formatCurrency(sellVolume)} ${t('whaleSummary.shortVsLong')} ${formatCurrency(buyVolume)} ${t('whaleTrades.long').toLowerCase()})`
      };
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    if (value === undefined || value === null || isNaN(value)) {
      return '0';
    }

    const isNegative = value < 0;
    const absValue = Math.abs(value);

    let formatted;
    if (absValue >= 1e9) {
      formatted = `${(absValue / 1e9).toFixed(2)}B`;
    } else if (absValue >= 1e6) {
      formatted = `${(absValue / 1e6).toFixed(1)}M`;
    } else if (absValue >= 1e3) {
      formatted = `${(absValue / 1e3).toFixed(0)}K`;
    } else {
      formatted = absValue.toFixed(0);
    }

    return isNegative ? `-${formatted}` : formatted;
  };

  if (!show) return null;

  return (
    <div className={`whale-summary-overlay ${show ? 'show' : ''}`} onClick={onClose}>
      <div className={`whale-summary-modal ${show ? 'show' : ''}`} onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="whale-summary-close" onClick={onClose}>×</button>

        {/* Header */}
        <div className="whale-summary-header">
          <div className="whale-summary-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
              <path d="M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22ZM12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20ZM13 12H17L12 5V11H7L12 18V12Z"/>
            </svg>
          </div>
          <div className="whale-summary-title-container">
            <h2 className="whale-summary-title">{t('whaleSummary.title')}</h2>
            {lastUpdateText && (
              <div className="whale-summary-updated">{t('whaleSummary.updated')} {lastUpdateText}</div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="whale-summary-loading">
            <div className="spinner-border text-light" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
          </div>
        ) : transformedSummary ? (
          <div className="whale-summary-content">
            {/* Total Volume Card */}
            <div className="summary-card card-pulse">
              <div className="summary-card-label">{t('whaleSummary.totalVolume')}</div>
              <div className="summary-card-value">{formatCurrency(animatedValues.totalVolume)} USD</div>
              <div className="summary-card-sublabel">{transformedSummary.tradeCount} {t('whaleSummary.trades')}</div>
            </div>

            {/* Largest Trade Card */}
            <div className="summary-card card-slide-left">
              <div className="summary-card-label">{t('whaleSummary.largestTrade')}</div>
              <div className="summary-card-value">
                {transformedSummary.largestTrade ? formatCurrency(animatedValues.largestTrade) : '0'} USD
              </div>
              {transformedSummary.largestTrade && (
                <div className="summary-card-sublabel">
                  {transformedSummary.largestTrade.symbol.replace('USDT', '').replace('USD', '')} •{' '}
                  <span className={transformedSummary.largestTrade.side === 'buy' ? 'text-success' : 'text-danger'}>
                    {transformedSummary.largestTrade.side === 'buy' ? t('whaleTrades.long') : t('whaleTrades.short')}
                  </span>
                </div>
              )}
            </div>

            {/* Net Flow Card */}
            <div className="summary-card card-slide-right">
              <div className="summary-card-label">{t('whaleSummary.netFlow')}</div>
              <div className={`summary-card-value ${animatedValues.netFlow >= 0 ? 'text-success' : 'text-danger'}`}>
                {animatedValues.netFlow >= 0 ? '+' : ''}{formatCurrency(animatedValues.netFlow)} USD
              </div>
              <div className="summary-card-sublabel">
                {t('whaleSummary.buy')}: {formatCurrency(transformedSummary.buyVolume)} • {t('whaleSummary.sell')}: {formatCurrency(transformedSummary.sellVolume)}
              </div>
            </div>

            {/* Market Bias Indicator */}
            {(() => {
              const marketBias = calculateMarketBias(transformedSummary.buyVolume, transformedSummary.sellVolume);
              return (
                <div className={`market-bias-indicator bias-${marketBias.bias} card-fade-in`}>
                  <div className="bias-icon">
                    {marketBias.bias === 'long' ? '📈' :
                     marketBias.bias === 'short' ? '📉' : '⚖️'}
                  </div>
                  <div className="bias-content">
                    <div className="bias-label">{t('whaleSummary.marketSentiment')}</div>
                    <div className="bias-message">{marketBias.message}</div>
                  </div>
                </div>
              );
            })()}

            {/* Momentum Indicator */}
            {transformedSummary.momentum && (
              <div className={`momentum-indicator momentum-${transformedSummary.momentum.sentiment} card-fade-in`}>
                <div className="momentum-icon">
                  {transformedSummary.momentum.sentiment === 'bullish' ? '💡' :
                   transformedSummary.momentum.sentiment === 'bearish' ? '⚠️' : 'ℹ️'}
                </div>
                <div className="momentum-message">{transformedSummary.momentum.message}</div>
              </div>
            )}
          </div>
        ) : (
          <div className="whale-summary-error">
            <p>{t('whaleSummary.noData')}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default WhaleSummaryModal;
